import React from 'react';
import { liveQuery, Subscription } from 'dexie';
import './global_notification_coordinator.css';
import { db } from '../../../myt_chat/chat/database/MytChatDB';
import CareTicket from '../../../myt_chat/chat/models/CareTicket';
import UserRole from '../../../myt_chat/chat/enums/UserRole';
import MytNotification from '../../../myt_chat/notification';
import { getAuthUserInfoOrDefault } from '../../../myt_chat/network/cookie';

type ChatNotificationCoordinatorState = {
  toastMessage: string;
};

const MESSAGE_DEDUPE_TTL_MS = 30 * 1000;
const CROSS_TAB_CLAIM_DELAY_MS = 120;

type NotificationClaimMessage = {
  type: 'claim';
  messageId: string;
  timestamp: number;
  tabId: string;
};

type NotificationHandledMessage = {
  type: 'handled';
  messageId: string;
  timestamp: number;
  tabId: string;
};

type NotificationChannelMessage = NotificationClaimMessage | NotificationHandledMessage;

type PendingNotificationClaim = {
  ticket: CareTicket;
  bestClaim: NotificationClaimMessage;
  timer: number;
};

const getChatRoute = (role: UserRole) => {
  return role === UserRole.TEACHER ? '/teacher_chat' : '/student_chat';
};

class GlobalChatNotificationCoordinator extends React.Component<
  Record<string, never>,
  ChatNotificationCoordinatorState
> {
  private ticketSubscription?: Subscription;
  private notificationChannel: BroadcastChannel | null = null;
  private previousTickets = new Map<string, CareTicket>();
  private notifiedMessageIds = new Map<string, number>();
  private pendingClaims = new Map<string, PendingNotificationClaim>();
  private hasHydratedInitialTickets = false;
  private toastTimer: number | null = null;
  private readonly tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  state: ChatNotificationCoordinatorState = {
    toastMessage: ''
  };

  componentDidMount() {
    const authInfo = getAuthUserInfoOrDefault();

    if (authInfo.role !== UserRole.UNKNOWN) {
      MytNotification.webPushInit();
    }

    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      this.notificationChannel = new BroadcastChannel('com.myyogateacher.chat.notifications');
      this.notificationChannel.onmessage = (event: MessageEvent<NotificationChannelMessage>) => {
        this.handleCrossTabMessage(event.data);
      };
    }

    this.ticketSubscription = liveQuery(() => db.ticket.toArray()).subscribe(
      (tickets) => {
        this.syncTickets(tickets);
      },
      (error) => {
        console.log('Error in global chat notification subscription', error);
      }
    );
  }

  componentWillUnmount() {
    this.ticketSubscription?.unsubscribe();
    this.ticketSubscription = undefined;

    this.pendingClaims.forEach((claim) => {
      window.clearTimeout(claim.timer);
    });
    this.pendingClaims.clear();

    this.notificationChannel?.close();
    this.notificationChannel = null;

    if (this.toastTimer) {
      window.clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
  }

  private syncTickets = (tickets: CareTicket[]) => {
    const nextTickets = new Map(tickets.map((ticket) => [ticket.user, { ...ticket }]));

    if (!this.hasHydratedInitialTickets) {
      this.previousTickets = nextTickets;
      this.hasHydratedInitialTickets = true;
      return;
    }

    let notificationTicket: CareTicket | null = null;

    for (const ticket of tickets) {
      const previous = this.previousTickets.get(ticket.user);
      const currentMessageId = ticket.message?.id || '';
      const unreadIncreased = ticket.unread > (previous?.unread || 0);
      const messageChanged = !!ticket.message?.id && ticket.message.id !== previous?.message?.id;
      const isEligible =
        ticket.unread > 0 &&
        !!currentMessageId &&
        ticket.message?.from !== 'care' &&
        !ticket.message?.flags?.isAutomated;

      if (
        !notificationTicket &&
        isEligible &&
        (unreadIncreased || messageChanged) &&
        !this.hasRecentlyNotified(currentMessageId)
      ) {
        notificationTicket = ticket;
      }
    }

    this.previousTickets = nextTickets;

    if (notificationTicket) {
      this.queueNotification(notificationTicket);
    }
  };

  private queueNotification = (ticket: CareTicket) => {
    const messageId = ticket.message.id;

    if (!messageId || this.hasRecentlyNotified(messageId) || this.pendingClaims.has(messageId)) {
      return;
    }

    if (!this.notificationChannel) {
      this.markMessageAsNotified(messageId);
      window.setTimeout(() => this.notifyForTicket(ticket), 0);
      return;
    }

    const claim: NotificationClaimMessage = {
      type: 'claim',
      messageId,
      timestamp: Date.now(),
      tabId: this.tabId
    };

    const timer = window.setTimeout(() => {
      const pending = this.pendingClaims.get(messageId);
      if (!pending) {
        return;
      }

      this.pendingClaims.delete(messageId);

      if (this.hasRecentlyNotified(messageId)) {
        return;
      }

      if (pending.bestClaim.tabId !== this.tabId) {
        return;
      }

      this.markMessageAsNotified(messageId);
      this.notificationChannel?.postMessage({
        type: 'handled',
        messageId,
        timestamp: Date.now(),
        tabId: this.tabId
      } satisfies NotificationHandledMessage);
      this.notifyForTicket(pending.ticket);
    }, CROSS_TAB_CLAIM_DELAY_MS);

    this.pendingClaims.set(messageId, {
      ticket,
      bestClaim: claim,
      timer
    });

    this.notificationChannel.postMessage(claim);
  };

  private notifyForTicket = (ticket: CareTicket) => {
    const { name, message } = ticket;
    const shown = MytNotification.showLocalNotification(
      ticket.name,
      `New message from ${name}: ${message.text || 'You have a new message'}`,
      () => {
        this.openTicket(ticket);
      }
    );

    if (!shown && typeof document !== 'undefined' && !document.hidden) {
      this.showToast(`New message from ${name}`);
    }
  };

  private openTicket = (ticket: CareTicket) => {
    const redirectUrl = getChatRoute(ticket.role);

    try {
      sessionStorage.setItem(
        'pendingChatOpen',
        JSON.stringify({
          role: ticket.role === UserRole.TEACHER ? 'teacher' : 'student',
          channelUuid: ticket.user,
          ts: Date.now()
        })
      );
    } catch (error) {
      console.log('Error setting pendingChatOpen from notification click', error);
    }

    if (!window.location.pathname.startsWith(redirectUrl)) {
      const qp = `?from=push&channel=${encodeURIComponent(ticket.user)}`;
      window.location.assign(`${redirectUrl}${qp}`);
      return;
    }

    window.dispatchEvent(new CustomEvent('chat:open', { detail: { channelUuid: ticket.user } }));
  };

  private showToast = (message: string) => {
    if (this.toastTimer) {
      window.clearTimeout(this.toastTimer);
    }

    this.setState({ toastMessage: message });
    this.toastTimer = window.setTimeout(() => {
      this.setState({ toastMessage: '' });
      this.toastTimer = null;
    }, 4000);
  };

  private hasRecentlyNotified = (messageId: string) => {
    const now = Date.now();

    this.notifiedMessageIds.forEach((timestamp, id) => {
      if (now - timestamp > MESSAGE_DEDUPE_TTL_MS) {
        this.notifiedMessageIds.delete(id);
      }
    });

    return this.notifiedMessageIds.has(messageId);
  };

  private markMessageAsNotified = (messageId: string) => {
    this.notifiedMessageIds.set(messageId, Date.now());
  };

  private handleCrossTabMessage = (message: NotificationChannelMessage) => {
    if (!message?.messageId || message.tabId === this.tabId) {
      return;
    }

    if (message.type === 'handled') {
      this.markMessageAsNotified(message.messageId);
      this.cancelPendingClaim(message.messageId);
      return;
    }

    const pending = this.pendingClaims.get(message.messageId);
    if (!pending) {
      return;
    }

    if (this.isClaimPreferred(message, pending.bestClaim)) {
      pending.bestClaim = message;
    }
  };

  private cancelPendingClaim = (messageId: string) => {
    const pending = this.pendingClaims.get(messageId);
    if (!pending) {
      return;
    }

    window.clearTimeout(pending.timer);
    this.pendingClaims.delete(messageId);
  };

  private isClaimPreferred = (
    incoming: NotificationClaimMessage,
    current: NotificationClaimMessage
  ) => {
    if (incoming.timestamp !== current.timestamp) {
      return incoming.timestamp < current.timestamp;
    }

    return incoming.tabId < current.tabId;
  };

  render() {
    if (!this.state.toastMessage) {
      return null;
    }

    return <div className="chat-global-toast">{this.state.toastMessage}</div>;
  }
}

export default GlobalChatNotificationCoordinator;
